import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import type { ScrollGlobeSection } from "./scrollGlobe.types";

export interface ScrollGlobeNavProps {
  sections: readonly ScrollGlobeSection[];
  activeSection: number;
  onSelect: (index: number) => void;
  /** `false` = la persona pidió movimiento reducido. */
  motionOk: boolean;
}

/**
 * La navegación lateral: un punto por sección, con su etiqueta.
 *
 * ── Es una `<nav>` con nombre ────────────────────────────────────────
 * El original es un `<div>` suelto de botones. Aquí es un punto de
 * referencia con `aria-label`, así que quien navega por regiones lo
 * encuentra y sabe qué es. `aria-current` marca la sección en la que se
 * está: sin él, la única señal de "estás aquí" es el color del punto.
 *
 * ── La etiqueta flotante ─────────────────────────────────────────────
 * Aparece al entrar en la sección y se desvanece sola (`animate-fadeOut`)
 * — es una pista, no un menú permanente. Con movimiento reducido no se
 * pinta: una etiqueta que se desvanece por su cuenta es exactamente el
 * tipo de movimiento no pedido que la preferencia viene a apagar, y el
 * nombre de cada punto sigue estando en su `aria-label` y en su `title`.
 *
 * ── Se esconde en móvil ──────────────────────────────────────────────
 * Cuatro puntos de 10 px pegados al borde derecho de un teléfono son un
 * objetivo imposible y roban ancho al contenido. En pantallas pequeñas se
 * navega desplazando, que es lo que la página pide de todos modos.
 */
export function ScrollGlobeNav({
  sections,
  activeSection,
  onSelect,
  motionOk,
}: ScrollGlobeNavProps) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t("Secciones de la página")}
      className="fixed right-2 top-1/2 z-40 hidden -translate-y-1/2 sm:right-4 sm:flex lg:right-8"
    >
      <ul className="space-y-3 sm:space-y-4 lg:space-y-6">
        {sections.map((section, index) => {
          const label = section.badge ?? t("Sección {{n}}", { n: index + 1 });
          const isActive = activeSection === index;

          return (
            <li key={section.id} className="relative">
              {motionOk && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute right-5 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg border border-ds-border",
                    "bg-white/95 px-3 py-1.5 text-[12px] font-medium text-ds-ink shadow-ds-card backdrop-blur-md",
                    "sm:right-6 lg:right-8",
                    isActive ? "animate-fadeOut" : "opacity-0",
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ds-violet-pop motion-reduce:animate-none" />
                    {label}
                  </span>
                </span>
              )}

              <button
                type="button"
                onClick={() => onSelect(index)}
                aria-current={isActive ? "true" : undefined}
                aria-label={t("Ir a {{seccion}}", { seccion: label })}
                title={label}
                className={cn(
                  "relative block h-2.5 w-2.5 rounded-full border-2 transition-all duration-300 lg:h-3 lg:w-3",
                  "hover:scale-125 motion-reduce:transform-none motion-reduce:transition-none",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ds-cream",
                  isActive
                    ? "border-ds-violet-pop bg-ds-violet-pop shadow-ds-card"
                    : "border-ds-lavender bg-transparent hover:border-ds-violet-pop hover:bg-ds-mist",
                )}
              />
            </li>
          );
        })}
      </ul>

      <span
        aria-hidden="true"
        className="absolute bottom-0 left-1/2 top-0 -z-10 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-ds-violet-pop/20 to-transparent"
      />
    </nav>
  );
}
